import React, { useEffect, useState } from 'react'
import { Box, useTheme, type SxProps, type Theme } from '@mui/material'

import { Image } from '@repo/ui'
import { useColorMode } from 'hooks'

interface IntegrationLogoProps {
  src?: string | null
  alt?: string
  /** Square tile size in px. Default 64. */
  size?: number
  /** Extra sx merged onto the tile. */
  sx?: SxProps<Theme>
  /** Rendered inside the tile when there's no logo (e.g. a first-letter avatar). */
  fallback?: React.ReactNode
}

/**
 * Logo tile with a luminance-adaptive treatment so brand marks read consistently
 * across themes (VERA-405):
 *  - light mode → white tile (as before),
 *  - dark mode  → dark tile (like company logos / the Connect modal), and a
 *    near-black logo is knocked out to white (`brightness(0) invert(1)`) so it
 *    stays visible instead of disappearing into the dark tile.
 *
 * Darkness is detected by sampling the image on a canvas (same approach as
 * LogoField). Cross-origin images without CORS headers can't be sampled — those
 * degrade gracefully to no-invert.
 */
export const IntegrationLogo = ({ src, alt = '', size = 64, sx, fallback }: IntegrationLogoProps) => {
  const theme = useTheme()
  const { resolvedMode } = useColorMode()
  const isDark = resolvedMode === 'dark'
  const [invert, setInvert] = useState(false)

  useEffect(() => {
    setInvert(false)
    if (!src || !isDark) return
    let cancelled = false
    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (cancelled) return
      try {
        const n = 24
        const canvas = document.createElement('canvas')
        canvas.width = n
        canvas.height = n
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctx.drawImage(img, 0, 0, n, n)
        const { data } = ctx.getImageData(0, 0, n, n)
        let lumSum = 0
        let opaque = 0
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 32) continue // skip transparent pixels
          opaque += 1
          lumSum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
        }
        // Average luminance < ~60/255 over opaque pixels → treat as a dark/black mark.
        if (opaque > 0 && lumSum / opaque < 60) setInvert(true)
      } catch {
        // tainted canvas (no CORS) — leave as-is
      }
    }
    img.src = src
    return () => {
      cancelled = true
    }
  }, [src, isDark])

  const tileSx = {
    borderRadius: 3,
    p: 2,
    minWidth: size,
    maxWidth: size,
    height: size,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Literal dark hex in dark mode (the theme's grey scale flips in its dark
    // colorScheme, so grey[900] would resolve LIGHT here — same reason LogoField
    // uses #1f1f23). A dark tile keeps inverted (white) marks visible.
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.grey[200]}`,
    backgroundColor: isDark ? '#1f1f23' : theme.palette.common.white,
    ...sx,
  } as SxProps<Theme>

  return (
    <Box sx={tileSx}>
      {src ? (
        <Image
          src={src}
          alt={alt}
          fit="contain"
          sx={{
            width: '100%',
            height: '100%',
            filter: invert ? 'brightness(0) invert(1)' : undefined,
          }}
        />
      ) : (
        fallback ?? null
      )}
    </Box>
  )
}
